/**
 * Fire on Teslyn Cloud's staff maps (decision 806; docs/specs/cloud/fire-feeds.md §5): the Operations Hub and a
 * project's map. Teslyn Cloud reads the state agencies' feeds and DEA Hotspots on the server (the fire poller); a map
 * reads `cloud_fires` each minute while a fire layer is ticked, which keeps the feeds asked for. Both layers are off
 * until ticked (decision 477), chosen per viewer in this browser; the state fires are offered only while the
 * organisation's switch is on (decision 805). Information only, not a safety system, on the layer and in every popup.
 * Staff only: the client portal never shows this.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { SymbolGlyph } from './SymbolGlyph.tsx';
import {
  FIRE_FEEDS_SWITCH,
  FIRE_NOTICE,
  HOTSPOT_NOTICE,
  agoText,
  cloudFireLayers,
  fireLinkAllowed,
  readCloudFires,
  type CloudFireLayers,
  type CloudFiresAnswer,
  type MapFeature,
} from '@teslyn/domain';
import { textNode } from './leaflet.ts';

/** A function Teslyn Cloud does not have yet (the migration not applied): PostgREST's PGRST202, Postgres 42883. */
const notThereYet = (e: { code?: string; message?: string } | null): boolean =>
  !!e &&
  (e.code === 'PGRST202' || e.code === '42883' || /could not find the function/i.test(e.message ?? ''));

/** `cloud_fires(tenant)`, read; null before the migration is applied (nothing to show, nothing said). */
export async function loadCloudFires(sb: SupabaseClient, tenantId: string): Promise<CloudFiresAnswer | null> {
  const r = (await sb.rpc('cloud_fires', { p_tenant: tenantId })) as {
    data: unknown;
    error: { code?: string; message?: string } | null;
  };
  if (notThereYet(r.error)) return null;
  if (r.error) throw new Error(r.error.message ?? 'Teslyn Cloud could not read the fires.');
  return readCloudFires(r.data);
}

/**
 * The organisation's state feeds switch alone (its staff read their own `tenant_service_switch` rows), so the ticks
 * can be offered without asking for the fires: `cloud_fires` is called only while a layer is ticked, because each call
 * keeps the poller asking for 15 minutes.
 */
export async function loadFireSwitch(sb: SupabaseClient, tenantId: string): Promise<boolean> {
  const r = (await sb
    .from('tenant_service_switch')
    .select('enabled')
    .eq('tenant_id', tenantId)
    .eq('service', FIRE_FEEDS_SWITCH)) as { data: { enabled?: boolean }[] | null; error: unknown };
  return !r.error && (r.data ?? []).some((x) => x.enabled === true);
}

/** Nothing ticked: only whether the state fires may be offered. */
const switchOnly = (on: boolean): CloudFiresAnswer => ({
  fireFeeds: on,
  feeds: [],
  cells: [],
  hotspotsFetchedAt: null,
  hotspotsFull: false,
});

export interface FireChoice {
  readonly fires: boolean;
  readonly hotspots: boolean;
}
const NONE: FireChoice = { fires: false, hotspots: false };
const KEY = 'teslyn.cloud.fire-layers';

/** This viewer's ticks in this browser (off at first, decision 477). */
export function useFireChoice(): [FireChoice, (c: FireChoice) => void] {
  const [c, setC] = useState<FireChoice>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<FireChoice> | null;
      return { fires: v?.fires === true, hotspots: v?.hotspots === true };
    } catch {
      return NONE;
    }
  });
  const set = (next: FireChoice): void => {
    setC(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable: the choice lasts until the page closes */
    }
  };
  return [c, set];
}

export interface CloudFires {
  readonly answer: CloudFiresAnswer | null;
  readonly layers: CloudFireLayers;
  readonly error: string | null;
  readonly nowMs: number;
}

const REFRESH_MS = 60_000;

/** The organisation's fires while a layer is ticked, read each minute; with none ticked, only its switch. */
export function useCloudFires(
  sb: SupabaseClient,
  tenantId: string | null,
  choice: FireChoice,
  clock: () => number = Date.now,
): CloudFires {
  const [answer, setAnswer] = useState<CloudFiresAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [nowMs, setNowMs] = useState(clock);
  const wanted = choice.fires || choice.hotspots;
  useEffect(() => {
    if (!tenantId) return undefined;
    let live = true;
    (wanted ? loadCloudFires(sb, tenantId) : loadFireSwitch(sb, tenantId).then(switchOnly))
      .then((a) => {
        if (!live) return;
        setAnswer(a);
        setError(null);
        setNowMs(clock());
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    const t = wanted ? setTimeout(() => setTick((n) => n + 1), REFRESH_MS) : null;
    return () => {
      live = false;
      if (t) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the clock is read at each refresh
  }, [sb, tenantId, wanted, tick]);
  const layers = useMemo(() => cloudFireLayers(answer, nowMs, choice), [answer, nowMs, choice]);
  return { answer, layers, error, nowMs };
}

/** A fire or hot spot's popup: its identify rows as text, and its page when it is one of the agencies' or DEA's. */
export function firePopup(f: MapFeature): HTMLElement {
  const box = document.createElement('div');
  box.className = 'fire-popup';
  const dl = document.createElement('dl');
  for (const r of f.identify) {
    const row = document.createElement('div');
    row.appendChild(textNode(r.label, 'label'));
    row.appendChild(textNode(r.value, 'caption'));
    dl.appendChild(row);
  }
  box.appendChild(dl);
  if (f.link && fireLinkAllowed(f.link.url)) {
    const a = document.createElement('a');
    a.href = f.link.url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.referrerPolicy = 'no-referrer';
    a.textContent = f.link.label;
    box.appendChild(a);
  }
  return box;
}

/** The fire layers' ticks, notices, state, legend and credits under a staff map. */
export function FireControls({
  fires,
  choice,
  onChoice,
}: {
  readonly fires: CloudFires;
  readonly choice: FireChoice;
  readonly onChoice: (c: FireChoice) => void;
}): ReactElement | null {
  const a = fires.answer;
  if (!a && !fires.error) return null;
  const { layers } = fires;
  const feedAge = a?.feeds.length
    ? agoText(
        a.feeds
          .map((f) => f.fetchedAt)
          .sort()
          .at(-1)!,
        fires.nowMs,
      )
    : null;
  return (
    <fieldset className="fire-controls" aria-label="Fire on the map">
      <legend className="label">Fire on the map</legend>
      {a?.fireFeeds && (
        <label className="body">
          <input
            type="checkbox"
            checked={choice.fires}
            onChange={(e) => onChoice({ ...choice, fires: e.target.checked })}
          />{' '}
          Fires (state agencies)
        </label>
      )}
      {choice.fires && a?.fireFeeds && (
        <p className="caption" role="note">
          {FIRE_NOTICE}{' '}
          {feedAge
            ? `Teslyn Cloud fetched the feeds ${feedAge}.`
            : 'Teslyn Cloud asks the feeds within 5 minutes of the map opening.'}
          {layers.fires
            ? ` Emergency Warning ${layers.fires.counts.emergency_warning}, Watch and Act ${layers.fires.counts.watch_and_act}, Advice ${layers.fires.counts.advice}, no warning ${layers.fires.counts.none}.`
            : ''}
        </p>
      )}
      <label className="body">
        <input
          type="checkbox"
          checked={choice.hotspots}
          onChange={(e) => onChoice({ ...choice, hotspots: e.target.checked })}
        />{' '}
        Satellite hot spots (DEA)
      </label>
      {choice.hotspots && (
        <p className="caption" role="note">
          {HOTSPOT_NOTICE}{' '}
          {a?.hotspotsFetchedAt
            ? `Teslyn Cloud fetched them ${agoText(a.hotspotsFetchedAt, fires.nowMs)}.`
            : 'Teslyn Cloud asks DEA within 5 minutes of the map opening, round each project in progress.'}
          {a?.hotspotsFull ? ' More hot spots than one answer holds: some are not shown.' : ''}
        </p>
      )}
      {fires.error && (
        <p className="caption" role="status">
          The fires could not be read: {fires.error}
        </p>
      )}
      {layers.legend.length > 0 && (
        <ul className="legend" aria-label="Fire legend">
          {layers.legend.map((e) => (
            <li key={e.word} className="legend__item caption">
              {e.symbol ? (
                <SymbolGlyph symbol={e.symbol} colour={e.colour} />
              ) : (
                <span
                  className={`legend__swatch legend__swatch--${e.stroke}${e.dashed ? ' legend__swatch--dashed' : ''}`}
                  style={{ borderColor: `var(--${e.colour})` }}
                  aria-hidden="true"
                />
              )}
              {e.word}
            </li>
          ))}
        </ul>
      )}
      {layers.credits.map((c) => (
        <p key={c} className="caption muted">
          {c}
        </p>
      ))}
    </fieldset>
  );
}
